import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import NotificationBell from '../../components/shared/NotificationBell'
import { triggerDailyNotifications } from '../../lib/notifications'

const roleConfig = {
  admin:   { label: 'Admin',      color: '#ef4444' },
  coach:   { label: 'Coach',      color: '#34d399' },
  athlete: { label: 'Atlet',      color: '#38bdf8' },
  parent:  { label: 'Orang Tua',  color: '#a78bfa' },
}

const formatDate = (dateStr) => {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
}

const formatTime = (dateStr) => {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })
}

const formatRelativeDate = (dateStr) => {
  if (!dateStr) return '—'
  const today = new Date(); today.setHours(0,0,0,0)
  const d = new Date(dateStr); d.setHours(0,0,0,0)
  const diff = Math.round((today - d) / 86400000)
  if (diff === 0) return 'Hari ini'
  if (diff === 1) return 'Kemarin'
  return formatDate(dateStr)
}

export default function AdminDashboard({ profile }) {
  const [activeTab, setActiveTab] = useState('overview')

  // ── State data real ────────────────────────────────────────
  const [stats, setStats]         = useState(null)
  const [recentLogs, setRecentLogs] = useState([])
  const [users, setUsers]         = useState([])
  const [badgeCounts, setBadgeCounts] = useState({ active: 0, hidden: 0 })
  const [loading, setLoading]     = useState(true)

  // ── Trigger notifikasi harian ──────────────────────────────
  useEffect(() => {
    if (!profile?.id) return
    const key = `notif_triggered_${new Date().toISOString().split('T')[0]}`
    if (!sessionStorage.getItem(key)) {
      triggerDailyNotifications()
      sessionStorage.setItem(key, '1')
    }
  }, [profile?.id])

  // ── Fetch semua data ───────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps 
  useEffect(() => {
    if (!profile?.id) return
    fetchAll()
  }, [profile?.id])

  const fetchAll = async () => {
    setLoading(true)
    try {
      await Promise.all([
        fetchStats(),
        fetchRecentLogs(),
        fetchUsers(),
        fetchBadgeCounts(),
      ])
    } finally {
      setLoading(false)
    }
  }

  // 1. Stats overview
  const fetchStats = async () => {
    // Total atlet
    const { count: totalAthletes } = await supabase
      .from('athletes')
      .select('id', { count: 'exact', head: true })

    // Total coach
    const { count: totalCoaches } = await supabase
      .from('profiles')
      .select('id', { count: 'exact', head: true })
      .eq('role', 'coach')

    // Sesi bulan ini
    const firstOfMonth = new Date()
    firstOfMonth.setDate(1); firstOfMonth.setHours(0,0,0,0)
    const { count: totalSesiMonth } = await supabase
      .from('session_logs')
      .select('id', { count: 'exact', head: true })
      .gte('created_at', firstOfMonth.toISOString())

    // Avg RPE bulan ini
    const { data: rpeData } = await supabase
      .from('session_logs')
      .select('rpe')
      .gte('created_at', firstOfMonth.toISOString())
      .not('rpe', 'is', null)

    const avgRPE = rpeData?.length
      ? (rpeData.reduce((s, r) => s + r.rpe, 0) / rpeData.length).toFixed(1)
      : '—'

    setStats({
      totalAthletes: totalAthletes ?? 0,
      totalCoaches:  totalCoaches  ?? 0,
      totalSesiMonth: totalSesiMonth ?? 0,
      avgRPE,
    })
  }

  // 2. Sesi terbaru (semua atlet, 5 terakhir)
  const fetchRecentLogs = async () => {
    const { data } = await supabase
      .from('session_logs')
      .select(`
        id, rpe, created_at,
        athletes ( profiles ( full_name ) ),
        sessions ( day_type, session_date )
      `)
      .order('created_at', { ascending: false })
      .limit(5)

    setRecentLogs(data || [])
  }

  // 3. Users (profiles lengkap)
  const fetchUsers = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, role, created_at')
      .order('created_at', { ascending: true })

    setUsers(data || [])
  }

  // 4. Badge counts
  const fetchBadgeCounts = async () => {
    const { data } = await supabase
      .from('badges')
      .select('id, is_hidden')

    if (data) {
      setBadgeCounts({
        active: data.filter(b => !b.is_hidden).length,
        hidden: data.filter(b =>  b.is_hidden).length,
      })
    }
  }

  // ── Label day_type ─────────────────────────────────────────
  const dayTypeLabel = (t) => ({
    hill_repeat: 'Hill Repeat',
    long_ride:   'Long Ride',
    road_passing: 'Road Passing',
    mtb_xc:       'MTB/XC',
    indoor:      'Indoor',
  }[t] ?? t ?? '—')

  // ── Render ─────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#f1f5f9' }}>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '1rem 1.5rem', borderBottom: '1px solid #1e293b', background: '#0a0f1e'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <img src="/logo_rollaco.png" alt="Rollaco"
            style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
          <div>
            <div style={{ color: '#ef4444', fontWeight: 'bold', fontSize: '1rem', letterSpacing: '0.05em' }}>
              ROLLACO
            </div>
            <div style={{ color: '#94a3b8', fontSize: '0.65rem', letterSpacing: '0.1em' }}>
              ADMIN PANEL
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: '#f1f5f9', fontSize: '0.9rem', fontWeight: 'bold' }}>
              {profile?.full_name}
            </div>
            <div style={{ color: '#ef4444', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Admin
            </div>
          </div>
          {profile?.id && <NotificationBell userId={profile.id} />}
          <button
            onClick={() => supabase.auth.signOut()}
            style={{
              background: '#1e293b', border: '1px solid #334155',
              borderRadius: '8px', color: '#94a3b8',
              padding: '0.4rem 0.75rem', cursor: 'pointer', fontSize: '0.8rem'
            }}
          >
            Keluar
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', borderBottom: '1px solid #1e293b',
        padding: '0 1.5rem', background: '#0a0f1e'
      }}>
        {[
          { id: 'overview', label: '📊 Overview' },
          { id: 'users',    label: '👥 Users' },
          { id: 'badges',   label: '🏆 Badge' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: '0.75rem 1rem', background: 'none', border: 'none',
            borderBottom: activeTab === tab.id ? '2px solid #ef4444' : '2px solid transparent',
            color: activeTab === tab.id ? '#ef4444' : '#94a3b8',
            cursor: 'pointer', fontSize: '0.8rem',
            fontWeight: activeTab === tab.id ? 'bold' : 'normal',
            marginBottom: '-1px'
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ padding: '1rem 1.5rem' }}>

        {/* ══════════════════════════════════════════
            TAB OVERVIEW
        ══════════════════════════════════════════ */}
        {activeTab === 'overview' && (
          <>
            {/* Stats grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
              {[
                { label: 'Total Atlet',     value: stats?.totalAthletes  ?? '…', icon: '🚴', color: '#38bdf8' },
                { label: 'Total Coach',     value: stats?.totalCoaches   ?? '…', icon: '👨‍🏫', color: '#34d399' },
                { label: 'Sesi Bulan Ini',  value: stats?.totalSesiMonth ?? '…', icon: '📊', color: '#facc15' },
                { label: 'Avg RPE',         value: stats?.avgRPE         ?? '…', icon: '💪', color: '#fb923c' },
              ].map(stat => (
                <div key={stat.label} style={{
                  background: `${stat.color}18`, border: `2px solid ${stat.color}44`,
                  borderRadius: '12px', padding: '1rem', textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.5rem' }}>{stat.icon}</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 'bold', color: stat.color }}>
                    {loading ? '…' : stat.value}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Sesi terbaru */}
            <div style={{
              background: '#1e293b', borderRadius: '12px',
              padding: '1rem', marginBottom: '1rem'
            }}>
              <div style={{ color: '#f1f5f9', fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                🕐 Sesi Terbaru
              </div>
              {loading ? (
                <div style={{ color: '#475569', fontSize: '0.8rem', textAlign: 'center', padding: '1rem' }}>
                  Memuat...
                </div>
              ) : recentLogs.length === 0 ? (
                <div style={{ color: '#475569', fontSize: '0.8rem', textAlign: 'center', padding: '1rem' }}>
                  Belum ada sesi
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {recentLogs.map(log => (
                    <div key={log.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '0.5rem 0.75rem', background: '#0f172a', borderRadius: '8px'
                    }}>
                      <div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                          {log.athletes?.profiles?.full_name ?? '—'}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                          {dayTypeLabel(log.sessions?.day_type)} • {formatRelativeDate(log.sessions?.session_date)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.8rem', color: '#fb923c', fontWeight: 'bold' }}>
                          RPE {log.rpe ?? '—'}
                        </div>
                        <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                          {formatTime(log.created_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick actions */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
              {[
                { label: 'Tambah User',    icon: '➕', color: '#34d399' },
                { label: 'Export Data',    icon: '📤', color: '#38bdf8' },
                { label: 'Kelola Badge',   icon: '🏆', color: '#facc15' },
                { label: 'Setting Jadwal', icon: '📅', color: '#a78bfa' },
              ].map(action => (
                <button key={action.label} onClick={() => {
                  if (action.label === 'Kelola Badge')   setActiveTab('badges')
                  if (action.label === 'Tambah User')    setActiveTab('users')
                }} style={{
                  padding: '0.75rem',
                  background: `${action.color}15`,
                  border: `2px dashed ${action.color}55`,
                  borderRadius: '10px', color: action.color,
                  cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold',
                  display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center'
                }}>
                  {action.icon} {action.label}
                </button>
              ))}
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════
            TAB USERS
        ══════════════════════════════════════════ */}
        {activeTab === 'users' && (
          <>
            {/* Count per role */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
              {Object.entries(roleConfig).map(([role, cfg]) => {
                const count = users.filter(u => u.role === role).length
                return (
                  <div key={role} style={{
                    padding: '0.35rem 0.75rem', borderRadius: '999px',
                    background: `${cfg.color}15`, border: `1px solid ${cfg.color}44`,
                    color: cfg.color, fontSize: '0.7rem', fontWeight: 'bold'
                  }}>
                    {cfg.label}: {loading ? '…' : count}
                  </div>
                )
              })}
            </div>

            {loading ? (
              <div style={{ color: '#475569', fontSize: '0.8rem', textAlign: 'center', padding: '2rem' }}>
                Memuat...
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {users.map(user => {
                  const rc = roleConfig[user.role] ?? { label: user.role, color: '#94a3b8' }
                  return (
                    <div key={user.id} style={{
                      background: '#1e293b', borderRadius: '10px',
                      padding: '0.75rem 1rem',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{user.full_name}</div>
                        <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '2px' }}>
                          Bergabung {formatDate(user.created_at)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{
                          fontSize: '0.65rem', padding: '2px 8px', borderRadius: '999px',
                          background: `${rc.color}22`, color: rc.color, fontWeight: 'bold'
                        }}>
                          {rc.label}
                        </span>
                        <button style={{
                          background: 'none', border: '1px solid #334155',
                          borderRadius: '6px', color: '#94a3b8',
                          padding: '2px 8px', cursor: 'pointer', fontSize: '0.65rem'
                        }}>
                          Edit
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            <button style={{
              width: '100%', marginTop: '0.75rem', padding: '0.75rem',
              background: '#ef444415', border: '2px dashed #ef444455',
              borderRadius: '10px', color: '#ef4444',
              cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold'
            }}>
              + Tambah User Baru
            </button>
          </>
        )}

        {/* ══════════════════════════════════════════
            TAB BADGES
        ══════════════════════════════════════════ */}
        {activeTab === 'badges' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
              <div style={{
                background: '#facc1518', border: '2px solid #facc1544',
                borderRadius: '12px', padding: '1rem', textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.5rem' }}>🏆</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#facc15' }}>
                  {loading ? '…' : badgeCounts.active}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Badge Normal</div>
              </div>
              <div style={{
                background: '#a78bfa18', border: '2px solid #a78bfa44',
                borderRadius: '12px', padding: '1rem', textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.5rem' }}>🔒</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#a78bfa' }}>
                  {loading ? '…' : badgeCounts.hidden}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Badge Hidden</div>
              </div>
            </div>

            {/* Info hutang teknis badge trigger */}
            <div style={{
              background: '#1e293b', borderRadius: '12px',
              padding: '1rem', marginBottom: '0.75rem'
            }}>
              <div style={{ color: '#f1f5f9', fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                ⚠️ Hutang Teknis
              </div>
              <div style={{
                background: '#f8717115', border: '1px solid #f8717144',
                borderRadius: '8px', padding: '0.75rem',
                fontSize: '0.75rem', color: '#f87171', lineHeight: 1.5
              }}>
                Badge trigger otomatis belum diimplementasi (Tahap 10c).
                Count badge di atas sudah real dari Supabase — tapi pemberian badge ke atlet masih manual.
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button style={{
                flex: 1, padding: '0.75rem',
                background: '#facc1515', border: '2px solid #facc1555',
                borderRadius: '10px', color: '#facc15',
                cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold'
              }}>
                🏆 Kelola Badge
              </button>
              <button style={{
                flex: 1, padding: '0.75rem',
                background: '#a78bfa15', border: '2px solid #a78bfa55',
                borderRadius: '10px', color: '#a78bfa',
                cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold'
              }}>
                🔒 Kelola Hidden
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

